import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Copy, Loader2, LogIn, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Column, DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdminImpersonation } from "@/hooks/useAdminImpersonation";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useSorting } from "@/hooks/useSorting";
import {
  hasPlatformPermission,
  isGuesteriaAdminRole,
  isSuperAdminRole,
  PlatformPermissionKey,
} from "@/utils/accessControl";
import { Hotel, HotelFilters } from "@/types/hotel";
import { Role } from "@/types/user";
import { UserProfile } from "@/types/user-profile";
import { useActiveDeletionJobForTarget } from "@/hooks/useDataDeletion";
import { DeletionDangerZone } from "@/components/common/data-deletion/DeletionDangerZone";
import { useDeletionFlow } from "@/components/common/data-deletion/useDeletionFlow";
import { DeletionTargetType } from "@/types/data-deletion";
import { hotelKeys, useHotels } from "../hotels/hooks/useHotels";
import { useUsers, useUsersCount } from "../users/hooks/useUsers";
import { TenantSsoSettingsTab } from "../sso-settings/TenantSsoSettingsTab";
import { TenantPartnersSection } from "./components/TenantPartnersSection";
import {
  tenantCatalogKeys,
  useTenant,
  useUpdateTenant,
  useUpdateTenantPayments,
} from "./hooks/useTenants";
import { useTenantSubscriptions } from "./hooks/useTenantSubscriptions";

const perPage = 10;

enum TenantDetailsTab {
  TENANT_INFO = "tenant-info",
  HOTELS = "hotels",
  USERS = "users",
  SSO = "sso",
}

const getStatusVariant = (status: string) => {
  if (status === "Active") {
    return "default";
  }
  if (status === "Suspended") {
    return "destructive";
  }
  return "secondary";
};

const activeSubscriptionStatuses = new Set(["active", "trialing"]);

function uniquePlanNames(
  planNames: Array<string | null | undefined>,
): string[] {
  const seen = new Set<string>();

  return planNames.reduce<string[]>((acc, planName) => {
    const normalizedPlanName = planName?.trim();

    if (!normalizedPlanName) {
      return acc;
    }

    const key = normalizedPlanName.toLowerCase();

    if (seen.has(key)) {
      return acc;
    }

    seen.add(key);
    acc.push(normalizedPlanName);
    return acc;
  }, []);
}

function getTenantPlanNames(
  tenantSubscriptions:
    | {
        status: string;
        cancelAtPeriodEnd?: boolean;
        plan: { title: string };
      }[]
    | undefined,
  tenantSubscriptionPlan: string | null | undefined,
  tenantSubscriptionPlans: string[] | undefined,
  tenantSubscriptionPlanNames: string[] | undefined,
): string[] {
  if (Array.isArray(tenantSubscriptions)) {
    return uniquePlanNames(
      tenantSubscriptions
        .filter(
          (subscription) =>
            !subscription.cancelAtPeriodEnd &&
            activeSubscriptionStatuses.has(subscription.status.toLowerCase()),
        )
        .map((subscription) => subscription.plan.title),
    );
  }

  if (
    Array.isArray(tenantSubscriptionPlans) &&
    tenantSubscriptionPlans.length
  ) {
    return uniquePlanNames(tenantSubscriptionPlans);
  }

  if (
    Array.isArray(tenantSubscriptionPlanNames) &&
    tenantSubscriptionPlanNames.length
  ) {
    return uniquePlanNames(tenantSubscriptionPlanNames);
  }

  return uniquePlanNames([tenantSubscriptionPlan]);
}

type TenantSubscriptionPlanBadge = {
  key: string;
  planName: string;
  hotelName?: string | null;
};

// Kept as its own component (rather than a ternary inline in
// `TenantEditPage`) so the visibility check doesn't add another branch to
// that component's cognitive-complexity budget, which is already at the
// sonarjs limit.
const TenantPartnersSlot: React.FC<{
  isPlatformAdmin: boolean;
  tenantId: string;
}> = ({ isPlatformAdmin, tenantId }) => {
  if (!isPlatformAdmin) {
    return null;
  }

  return <TenantPartnersSection tenantId={tenantId} />;
};

function canViewWithReadFallback(
  canAdministrateTenant: boolean,
  isPlatformAdmin: boolean,
  userProfile: Parameters<typeof hasPlatformPermission>[0],
  readPermission: PlatformPermissionKey,
): boolean {
  return (
    canAdministrateTenant ||
    (isPlatformAdmin && hasPlatformPermission(userProfile, readPermission))
  );
}

export const TenantEditPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenantId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [status, setStatus] = useState("");
  const [defaultRegion, setDefaultRegion] = useState("");
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hotelsPage, setHotelsPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [impersonatingUserId, setImpersonatingUserId] = useState<string | null>(
    null,
  );
  const { userProfile } = useAuthenticatedUser();
  const isSuperAdmin = isSuperAdminRole(userProfile);
  const isPlatformAdmin = isSuperAdmin || isGuesteriaAdminRole(userProfile);
  const canAdministrateTenant =
    isPlatformAdmin &&
    hasPlatformPermission(userProfile, "platform:tenants:administrate");
  const canViewTenantHotels = canAdministrateTenant;
  const canEditTenant = canAdministrateTenant;
  const canViewTenantUsers = canViewWithReadFallback(
    canAdministrateTenant,
    isPlatformAdmin,
    userProfile,
    "platform:users:read",
  );
  const canViewTenantSso = canViewWithReadFallback(
    canAdministrateTenant,
    isPlatformAdmin,
    userProfile,
    "platform:tenants:read",
  );
  const visibleTabs = useMemo(
    () => [
      TenantDetailsTab.TENANT_INFO,
      ...(canViewTenantUsers ? [TenantDetailsTab.USERS] : []),
      ...(canViewTenantHotels ? [TenantDetailsTab.HOTELS] : []),
      ...(canViewTenantSso ? [TenantDetailsTab.SSO] : []),
    ],
    [canViewTenantHotels, canViewTenantUsers, canViewTenantSso],
  );
  const {
    sortField: hotelSortField,
    sortDirection: hotelSortDirection,
    handleSort: handleHotelSort,
  } = useSorting();
  const {
    sortField: userSortField,
    sortDirection: userSortDirection,
    handleSort: handleUserSort,
  } = useSorting();
  const { mutateAsync: patchTenant } = useUpdateTenant();
  const { mutateAsync: patchPayments } = useUpdateTenantPayments();
  const { impersonateUser, isStartingImpersonation } = useAdminImpersonation();
  const queryClient = useQueryClient();
  const deletion = useDeletionFlow({
    onCompleted: (job) => {
      if (job.targetType === DeletionTargetType.TENANT) {
        queryClient.invalidateQueries({ queryKey: tenantCatalogKeys.all });
        navigate("/tenants");
      } else if (job.targetType === DeletionTargetType.HOTEL) {
        queryClient.invalidateQueries({ queryKey: hotelKeys.all });
      }
    },
  });

  const currentTab =
    (searchParams.get("tab") as TenantDetailsTab) ||
    TenantDetailsTab.TENANT_INFO;
  const selectedTab = visibleTabs.includes(currentTab)
    ? currentTab
    : TenantDetailsTab.TENANT_INFO;

  const {
    data: tenant,
    isLoading: isTenantLoading,
    isError,
  } = useTenant(tenantId, true);
  const { data: subscriptions = [] } = useTenantSubscriptions(
    tenantId,
    isSuperAdmin,
  );
  const { data: activeTenantJob } = useActiveDeletionJobForTarget(
    DeletionTargetType.TENANT,
    tenant?.id,
    isSuperAdmin,
  );

  const subscriptionPlanBadges = useMemo<TenantSubscriptionPlanBadge[]>(() => {
    const activeSubscriptions = subscriptions.filter(
      (subscription) =>
        !subscription.cancelAtPeriodEnd &&
        activeSubscriptionStatuses.has(subscription.status.toLowerCase()),
    );

    if (activeSubscriptions.length > 0) {
      return activeSubscriptions.map((subscription) => ({
        key: subscription.id,
        planName: subscription.plan.title,
        hotelName: subscription.hotel?.name ?? null,
      }));
    }

    const tenantPlanNames = getTenantPlanNames(
      tenant?.tenantSubscriptions,
      tenant?.subscriptionPlan,
      tenant?.subscriptionPlans,
      tenant?.subscriptionPlanNames,
    );

    return tenantPlanNames.map((planName, index) => ({
      key: `${planName}-${index}`,
      planName,
    }));
  }, [
    subscriptions,
    tenant?.tenantSubscriptions,
    tenant?.subscriptionPlan,
    tenant?.subscriptionPlanNames,
    tenant?.subscriptionPlans,
  ]);

  useEffect(() => {
    if (!tenant) {
      return;
    }

    setName(tenant.name);
    setSlug(tenant.slug);
    setStatus(tenant.status);
    setDefaultRegion(tenant.defaultRegion);
    setPaymentsEnabled(Boolean(tenant.paymentsEnabled));
  }, [tenant]);

  const hotelFilters: HotelFilters = useMemo(
    () => ({
      tenantId,
      currentPage: hotelsPage,
      perPage,
      sortField: hotelSortField,
      sortDirection: hotelSortDirection,
    }),
    [hotelSortDirection, hotelSortField, hotelsPage, tenantId],
  );

  const userFilters = useMemo(
    () => ({
      tenantId,
      roles: [Role.TENANT_ADMIN, Role.HOTEL_ADMIN, Role.FRONT_DESK],
      currentPage: usersPage,
      perPage,
      sortField: userSortField,
      sortDirection: userSortDirection,
    }),
    [tenantId, userSortDirection, userSortField, usersPage],
  );

  const { data: hotelsResponse, isLoading: areHotelsLoading } = useHotels(
    hotelFilters,
    canViewTenantHotels,
  );
  const { data: users = [], isLoading: areUsersLoading } = useUsers(
    userFilters,
    canViewTenantUsers,
  );
  const { data: usersTotal = 0 } = useUsersCount(
    userFilters,
    canViewTenantUsers,
  );

  const hotels = hotelsResponse?.data ?? [];
  const hotelsTotal = hotelsResponse?.total ?? 0;
  const hotelTotalPages = Math.max(1, Math.ceil(hotelsTotal / perPage));
  const userTotalPages = Math.max(1, Math.ceil(usersTotal / perPage));
  const activePlanByHotelId = useMemo(() => {
    const planByHotelId = new Map<string, string>();

    subscriptions
      .filter(
        (subscription) =>
          subscription.hotel?.id &&
          !subscription.cancelAtPeriodEnd &&
          activeSubscriptionStatuses.has(subscription.status.toLowerCase()),
      )
      .forEach((subscription) => {
        planByHotelId.set(subscription.hotel!.id, subscription.plan.title);
      });

    return planByHotelId;
  }, [subscriptions]);

  const hotelColumns: Column<Hotel>[] = [
    {
      key: "name",
      header: t("tenants.detail.hotels.columns.name"),
      sortable: true,
      render: (value: string) => <span>{value}</span>,
    },
    {
      key: "subscriptionPlan",
      header: t(
        "tenants.detail.hotels.columns.subscriptionPlan",
        "Subscription plan",
      ),
      render: (_: unknown, row: Hotel) =>
        activePlanByHotelId.get(row.id) ?? "—",
    },
    {
      key: "address",
      header: t("tenants.detail.hotels.columns.address"),
      sortable: true,
    },
    {
      key: "employees",
      header: t("tenants.detail.hotels.columns.users"),
      className: "text-center",
      render: (_: unknown, row: Hotel) => row.employees?.length ?? 0,
    },
    {
      key: "rooms",
      header: t("tenants.detail.hotels.columns.rooms"),
      className: "text-center",
      render: (_: unknown, row: Hotel) => row.rooms?.length ?? 0,
    },
    {
      key: "createdAt",
      header: t("tenants.detail.hotels.columns.createdAt"),
      className: "text-center",
      sortable: true,
      render: (value: Date | string) =>
        format(new Date(value), "dd MMM yyyy HH:mm"),
    },
    ...(isSuperAdmin
      ? [
          {
            key: "actions",
            header: t("users.table.actions"),
            className: "text-right",
            render: (_: unknown, row: Hotel) => (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                title={t("dataDeletion.dangerZone.hotel.rowAction")}
                aria-label={t("dataDeletion.dangerZone.hotel.rowAction")}
                onClick={() =>
                  deletion.startDeletion({
                    type: DeletionTargetType.HOTEL,
                    id: row.id,
                    label: row.name,
                    expectedConfirmation: row.name,
                    tenantId,
                  })
                }
              >
                <Trash2 className="text-custom-destructive-dark size-4" />
              </Button>
            ),
          } satisfies Column<Hotel>,
        ]
      : []),
  ];

  const handleImpersonateUser = useCallback(
    async (userId: string) => {
      setImpersonatingUserId(userId);

      try {
        await impersonateUser(userId);
      } finally {
        setImpersonatingUserId((currentUserId) =>
          currentUserId === userId ? null : currentUserId,
        );
      }
    },
    [impersonateUser],
  );

  const userColumns: Column<UserProfile>[] = [
    {
      key: "name",
      header: t("users.table.name"),
      render: (_: unknown, row: UserProfile) =>
        `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim() || "-",
    },
    {
      key: "email",
      header: t("users.table.email"),
      render: (value: string | undefined) => value || "-",
    },
    {
      key: "phone",
      header: t("users.table.phoneNumber"),
      render: (value: string | undefined) => value || "-",
    },
    {
      key: "role",
      header: t("users.table.role"),
      sortable: true,
      className: "text-center",
      render: (value: Role) => {
        if (value === Role.TENANT_ADMIN) {
          return t("users.table.tenantAdmin");
        }

        if (value === Role.HOTEL_ADMIN) {
          return t("users.table.hotelAdmin");
        }

        if (value === Role.FRONT_DESK) {
          return t("users.table.frontDesk");
        }

        return value;
      },
    },
    {
      key: "hotelId",
      header: t("tenants.detail.users.hotel"),
      render: (value: string | undefined) => value || "-",
    },
    ...(isSuperAdmin
      ? [
          {
            key: "id",
            header: t("users.table.actions"),
            className: "text-right",
            render: (_: string, row: UserProfile) => {
              const isCurrentUserImpersonationPending =
                isStartingImpersonation && impersonatingUserId === row.id;

              return (
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  title={t("tenants.detail.users.loginAsUser")}
                  aria-label={t("tenants.detail.users.loginAsUser")}
                  disabled={isStartingImpersonation}
                  onClick={() => {
                    void handleImpersonateUser(row.id);
                  }}
                >
                  {isCurrentUserImpersonationPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <LogIn className="h-4 w-4" />
                  )}
                </Button>
              );
            },
          } satisfies Column<UserProfile>,
        ]
      : []),
  ];

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!tenant || !canEditTenant) {
      return;
    }

    setIsSaving(true);

    try {
      await Promise.all([
        patchTenant({
          tenantId: tenant.id,
          dto: {
            name: name.trim() || undefined,
            slug: slug.trim() || undefined,
            status: status || undefined,
            defaultRegion: defaultRegion.trim() || undefined,
          },
        }),
        patchPayments({
          tenantId: tenant.id,
          dto: { paymentsEnabled },
        }),
      ]);

      toast.success(t("tenants.edit.successToast"));
    } catch {
      toast.error(t("tenants.edit.errorToast"));
    } finally {
      setIsSaving(false);
    }
  };

  if (isTenantLoading) {
    return (
      <div className="mx-auto flex max-w-[1344px] flex-col gap-6 p-12">
        <p className="text-muted-foreground text-sm">
          {t("tenants.detail.loading")}
        </p>
      </div>
    );
  }

  if (isError || !tenant) {
    return (
      <div className="mx-auto flex max-w-[1344px] flex-col gap-4 p-12">
        <Button
          variant="outline"
          className="w-fit"
          onClick={() => navigate("/tenants")}
        >
          {t("tenants.detail.backToTenants")}
        </Button>
        <div className="rounded-xl border p-6">
          <h1 className="text-xl font-semibold">
            {t("tenants.detail.notFoundTitle")}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            {t("tenants.detail.notFoundDescription")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col gap-6 p-12">
      <div className="bg-background flex flex-col gap-4 rounded-2xl border p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex flex-col gap-3">
            <Button
              variant="outline"
              className="w-fit"
              onClick={() => navigate("/tenants")}
            >
              {t("tenants.detail.backToTenants")}
            </Button>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-semibold tracking-tight">
                {tenant.name}
              </h1>
              <Badge variant={getStatusVariant(tenant.status)}>
                {tenant.status}
              </Badge>
            </div>
            <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-sm">
              <span>
                {t("tenants.detail.meta.slug")}: {tenant.slug}
              </span>
              <span>
                {t("tenants.detail.meta.region")}: {tenant.defaultRegion}
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <span>{t("tenants.detail.meta.plans")}:</span>
                {subscriptionPlanBadges.length === 0 ? (
                  <span>—</span>
                ) : (
                  subscriptionPlanBadges.map((plan) => (
                    <Badge key={plan.key} variant="new_secondary">
                      {plan.hotelName
                        ? `${plan.hotelName}: ${plan.planName}`
                        : plan.planName}
                    </Badge>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col items-start gap-2 rounded-xl border p-4 text-sm md:min-w-[280px]">
            <div className="flex w-full items-center justify-between gap-3">
              <span className="text-muted-foreground">
                {t("tenants.detail.meta.id")}
              </span>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => {
                  void (async () => {
                    try {
                      await navigator.clipboard.writeText(tenant.id);
                      toast.success(t("tenants.table.copyIdSuccess"));
                    } catch {
                      toast.error(t("tenants.table.copyIdError"));
                    }
                  })();
                }}
              >
                <Copy className="h-4 w-4" />
                {t("tenants.table.copyTenantId")}
              </Button>
            </div>
            <p className="text-muted-foreground font-mono text-xs break-all">
              {tenant.id}
            </p>
            <p>
              {t("tenants.detail.meta.createdAt")}:{" "}
              {format(new Date(tenant.createdAt), "dd MMM yyyy HH:mm")}
            </p>
            <p>
              {t("tenants.detail.meta.updatedAt")}:{" "}
              {format(new Date(tenant.updatedAt), "dd MMM yyyy HH:mm")}
            </p>
          </div>
        </div>
      </div>

      <Tabs
        value={selectedTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <TabsList className="h-auto bg-transparent p-0">
          <div className="flex flex-wrap gap-3">
            <TabsTrigger
              value={TenantDetailsTab.TENANT_INFO}
              className="bg-muted data-[state=active]:border-border data-[state=active]:bg-background rounded-lg border px-4 py-2"
            >
              {t("tenants.detail.tabs.tenantInfo")}
            </TabsTrigger>
            {canViewTenantUsers ? (
              <TabsTrigger
                value={TenantDetailsTab.USERS}
                className="bg-muted data-[state=active]:border-border data-[state=active]:bg-background rounded-lg border px-4 py-2"
              >
                {t("tenants.detail.tabs.users")}
              </TabsTrigger>
            ) : null}
            {canViewTenantHotels ? (
              <TabsTrigger
                value={TenantDetailsTab.HOTELS}
                className="bg-muted data-[state=active]:border-border data-[state=active]:bg-background rounded-lg border px-4 py-2"
              >
                {t("tenants.detail.tabs.hotels")}
              </TabsTrigger>
            ) : null}
            {canViewTenantSso ? (
              <TabsTrigger
                value={TenantDetailsTab.SSO}
                className="bg-muted data-[state=active]:border-border data-[state=active]:bg-background rounded-lg border px-4 py-2"
              >
                {t("tenants.detail.tabs.sso")}
              </TabsTrigger>
            ) : null}
          </div>
        </TabsList>

        <TabsContent value={TenantDetailsTab.TENANT_INFO} className="mt-6">
          <div className="flex flex-col gap-6">
            <form
              onSubmit={handleSubmit}
              className="bg-background flex flex-col gap-6 rounded-2xl border p-6"
            >
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label={t("tenants.edit.name")}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={!canEditTenant}
                />
                <Input
                  label={t("tenants.edit.slug")}
                  value={slug}
                  onChange={(event) => setSlug(event.target.value)}
                  disabled={!canEditTenant}
                />
                <Input
                  label={t("tenants.edit.defaultRegion")}
                  value={defaultRegion}
                  onChange={(event) => setDefaultRegion(event.target.value)}
                  disabled={!canEditTenant}
                />

                <div className="flex flex-col gap-1.5">
                  <span className="text-sm leading-none font-medium">
                    {t("tenants.edit.status")}
                  </span>
                  <Select
                    value={status}
                    onValueChange={setStatus}
                    disabled={!canEditTenant}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">pending</SelectItem>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-xl border p-4">
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium">
                    {t("tenants.edit.paymentsSection")}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {t("tenants.edit.paymentsEnabled")}
                  </p>
                </div>
                <Switch
                  checked={paymentsEnabled}
                  onCheckedChange={setPaymentsEnabled}
                  disabled={!canEditTenant}
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/tenants")}
                  disabled={isSaving}
                >
                  {t("tenants.detail.backToTenants")}
                </Button>
                {canEditTenant ? (
                  <Button type="submit" disabled={isSaving}>
                    {t("tenants.edit.save")}
                  </Button>
                ) : null}
              </div>
            </form>

            <TenantPartnersSlot
              isPlatformAdmin={isPlatformAdmin}
              tenantId={tenantId}
            />
          </div>
        </TabsContent>

        <TabsContent value={TenantDetailsTab.HOTELS} className="mt-6">
          <div className="bg-background rounded-2xl border p-6">
            <div className="mb-4 flex justify-end"></div>
            <DataTable
              data={hotels}
              columns={hotelColumns}
              loading={areHotelsLoading}
              pagination={{
                currentPage: hotelsPage,
                totalPages: hotelTotalPages,
                onPageChange: setHotelsPage,
              }}
              sorting={{
                field: hotelSortField,
                direction: hotelSortDirection,
                onSort: handleHotelSort,
              }}
              emptyMessage={t("tenants.detail.hotels.empty")}
            />
          </div>
        </TabsContent>

        <TabsContent value={TenantDetailsTab.USERS} className="mt-6">
          <div className="bg-background rounded-2xl border p-6">
            <div className="mb-4 flex justify-end gap-3"></div>
            <DataTable
              data={users}
              columns={userColumns}
              loading={areUsersLoading}
              pagination={{
                currentPage: usersPage,
                totalPages: userTotalPages,
                onPageChange: setUsersPage,
              }}
              sorting={{
                field: userSortField,
                direction: userSortDirection,
                onSort: handleUserSort,
              }}
              emptyMessage={t("tenants.detail.users.empty")}
            />
          </div>
        </TabsContent>

        {canViewTenantSso ? (
          <TabsContent value={TenantDetailsTab.SSO} className="mt-6">
            <TenantSsoSettingsTab
              tenantId={tenantId}
              canWrite={canAdministrateTenant}
            />
          </TabsContent>
        ) : null}
      </Tabs>

      {isSuperAdmin ? (
        <DeletionDangerZone
          targetType={DeletionTargetType.TENANT}
          description={t("dataDeletion.dangerZone.tenant.description")}
          buttonLabel={t("dataDeletion.dangerZone.tenant.button")}
          inFlight={Boolean(activeTenantJob)}
          onViewInFlight={
            activeTenantJob
              ? () => deletion.viewJob(activeTenantJob.id)
              : undefined
          }
          onDelete={() =>
            deletion.startDeletion({
              type: DeletionTargetType.TENANT,
              id: tenant.id,
              label: tenant.slug,
              expectedConfirmation: tenant.slug,
            })
          }
        />
      ) : null}

      {deletion.dialogs}
    </div>
  );
};
